// Чистая геометрия для карты: границы, проекция в прямоугольник фолбэка, отрезки линий.
import { DALAT_CENTER } from './styles'
import type { LatLng, MapPath, MapPin, MapTrail } from './types'

export interface Bounds {
  minLat: number
  maxLat: number
  minLng: number
  maxLng: number
}

const MIN_SPAN = 0.004 // ~450 м — чтобы одна точка не давала нулевой охват

export function isValidPoint(p: Partial<LatLng> | null | undefined): p is LatLng {
  return !!p && typeof p.lat === 'number' && typeof p.lng === 'number' && Number.isFinite(p.lat) && Number.isFinite(p.lng)
}

/** Охват точек с минимальным размером. Нет точек — центр Далата. */
export function computeBounds(points: readonly (Partial<LatLng> | null | undefined)[]): Bounds {
  const pts = points.filter(isValidPoint)
  if (!pts.length) {
    return {
      minLat: DALAT_CENTER.lat - MIN_SPAN * 2,
      maxLat: DALAT_CENTER.lat + MIN_SPAN * 2,
      minLng: DALAT_CENTER.lng - MIN_SPAN * 2,
      maxLng: DALAT_CENTER.lng + MIN_SPAN * 2,
    }
  }
  let minLat = Infinity
  let maxLat = -Infinity
  let minLng = Infinity
  let maxLng = -Infinity
  for (const p of pts) {
    minLat = Math.min(minLat, p.lat)
    maxLat = Math.max(maxLat, p.lat)
    minLng = Math.min(minLng, p.lng)
    maxLng = Math.max(maxLng, p.lng)
  }
  if (maxLat - minLat < MIN_SPAN) {
    const c = (maxLat + minLat) / 2
    minLat = c - MIN_SPAN / 2
    maxLat = c + MIN_SPAN / 2
  }
  if (maxLng - minLng < MIN_SPAN) {
    const c = (maxLng + minLng) / 2
    minLng = c - MIN_SPAN / 2
    maxLng = c + MIN_SPAN / 2
  }
  return { minLat, maxLat, minLng, maxLng }
}

/**
 * Проекция точки в проценты прямоугольника (как x/y макета). inset — поля в процентах,
 * чтобы маркеры у края не обрезались. Север сверху.
 */
export function projectPct(p: LatLng, b: Bounds, inset = 12): { x: number; y: number } {
  const span = 100 - inset * 2
  const x = inset + ((p.lng - b.minLng) / (b.maxLng - b.minLng)) * span
  const y = inset + ((b.maxLat - p.lat) / (b.maxLat - b.minLat)) * span
  return { x, y }
}

export interface Segment {
  x: number
  y: number
  len: number
  ang: number
  dashed: boolean
}

/**
 * Отрезки ломаной в пикселях (для фолбэка): первый участок — пунктир, остальные — сплошные (mapModel макета).
 * firstLeg — индекс точки, где кончается первый участок (путь по дорогам — много точек на участок).
 */
export function segments(path: readonly LatLng[], b: Bounds, w: number, h: number, inset = 12, firstLeg = 1): Segment[] {
  const pts = path.filter(isValidPoint).map((p) => {
    const q = projectPct(p, b, inset)
    return [(q.x / 100) * w, (q.y / 100) * h] as const
  })
  const out: Segment[] = []
  for (let i = 0; i < pts.length - 1; i++) {
    const [x1, y1] = pts[i]!
    const [x2, y2] = pts[i + 1]!
    out.push({
      x: x1,
      y: y1,
      len: Math.hypot(x2 - x1, y2 - y1),
      ang: (Math.atan2(y2 - y1, x2 - x1) * 180) / Math.PI,
      dashed: i < firstLeg,
    })
  }
  return out
}

/** GeoJSON маршрута: первый участок (пунктир, до точки firstLeg) и остальной путь. */
export function routeGeoJson(path: readonly LatLng[], firstLeg = 1) {
  const pts = path.filter(isValidPoint).map((p) => [p.lng, p.lat] as [number, number])
  const k = Math.min(Math.max(1, firstLeg), pts.length - 1)
  const features: {
    type: 'Feature'
    properties: { part: 'first' | 'rest' }
    geometry: { type: 'LineString'; coordinates: [number, number][] }
  }[] = []
  if (pts.length >= 2) {
    features.push({ type: 'Feature', properties: { part: 'first' }, geometry: { type: 'LineString', coordinates: pts.slice(0, k + 1) } })
  }
  if (pts.length >= k + 2) {
    features.push({ type: 'Feature', properties: { part: 'rest' }, geometry: { type: 'LineString', coordinates: pts.slice(k) } })
  }
  return { type: 'FeatureCollection' as const, features }
}

/** Координаты точки в пикселях мира Web Mercator при масштабе zoom (тайл 512, как у MapLibre). */
export function worldPx(p: LatLng, zoom: number): { x: number; y: number } {
  const size = 512 * 2 ** zoom
  const s = Math.sin((Math.max(-85, Math.min(85, p.lat)) * Math.PI) / 180)
  return {
    x: ((p.lng + 180) / 360) * size,
    y: (0.5 - Math.log((1 + s) / (1 - s)) / (4 * Math.PI)) * size,
  }
}

export interface PointGroup<T extends LatLng> {
  lat: number
  lng: number
  items: T[]
}

/**
 * Группы точек ближе radius пикселей друг к другу при масштабе zoom (жадно, в порядке входа: точка уходит
 * в первую группу, чей центр рядом). Сдвиг карты группы не меняет — только масштаб. Центр группы — среднее.
 */
export function clusterPoints<T extends LatLng>(items: readonly T[], zoom: number, radius: number): PointGroup<T>[] {
  const groups: { x: number; y: number; items: T[] }[] = []
  for (const it of items) {
    if (!isValidPoint(it)) continue
    const q = worldPx(it, zoom)
    const g = groups.find((c) => Math.hypot(c.x - q.x, c.y - q.y) <= radius)
    if (g) {
      g.items.push(it)
      g.x += (q.x - g.x) / g.items.length
      g.y += (q.y - g.y) / g.items.length
    } else groups.push({ x: q.x, y: q.y, items: [it] })
  }
  return groups.map((g) => ({
    lat: g.items.reduce((s, p) => s + p.lat, 0) / g.items.length,
    lng: g.items.reduce((s, p) => s + p.lng, 0) / g.items.length,
    items: g.items,
  }))
}

/**
 * Пины с полем cluster собираются в группы «+N» (до трёх цветов типов внутри); остальные и одиночки — как есть.
 * onGroup(центр) — тап по группе (карта приближает).
 */
export function groupPins(pins: readonly MapPin[], zoom: number, radius: number, onGroup?: (c: LatLng) => void): MapPin[] {
  const free = pins.filter((p) => !p.cluster)
  const out: MapPin[] = [...free]
  for (const g of clusterPoints(
    pins.filter((p) => p.cluster),
    zoom,
    radius,
  )) {
    if (g.items.length === 1) {
      out.push(g.items[0]!)
      continue
    }
    const colors = [...new Set(g.items.map((p) => p.cluster!))].slice(0, 3)
    const c = { lat: g.lat, lng: g.lng }
    out.push({
      kind: 'cluster',
      id: 'cluster:' + g.items.map((p, i) => p.id ?? i).join(','),
      lat: g.lat,
      lng: g.lng,
      count: g.items.length,
      colors,
      onClick: onGroup ? () => onGroup(c) : undefined,
    })
  }
  return out
}

/** GeoJSON троп: линия на тропу, selected — свойство для толщины. */
export function trailsGeoJson(trails: readonly MapTrail[]) {
  return {
    type: 'FeatureCollection' as const,
    features: trails
      .map((t) => ({ t, pts: t.points.filter(isValidPoint).map((p) => [p.lng, p.lat] as [number, number]) }))
      .filter((x) => x.pts.length >= 2)
      .map(({ t, pts }) => ({
        type: 'Feature' as const,
        properties: { id: t.id, selected: Boolean(t.selected) },
        geometry: { type: 'LineString' as const, coordinates: pts },
      })),
  }
}

/** Линии рисуемого маршрута → GeoJSON; dashed — прямой отрезок без дороги. */
export function pathsGeoJson(paths: readonly MapPath[]) {
  return {
    type: 'FeatureCollection' as const,
    features: paths
      .map((t) => ({ t, pts: t.points.filter(isValidPoint).map((p) => [p.lng, p.lat] as [number, number]) }))
      .filter((x) => x.pts.length >= 2)
      .map(({ t, pts }) => ({
        type: 'Feature' as const,
        properties: { id: t.id, dashed: Boolean(t.dashed) },
        geometry: { type: 'LineString' as const, coordinates: pts },
      })),
  }
}

/** Подпись набора точек: меняется — карта перевписывается. */
export function pointsSignature(points: readonly (Partial<LatLng> | null | undefined)[]): string {
  return points
    .filter(isValidPoint)
    .map((p) => `${p.lat.toFixed(4)},${p.lng.toFixed(4)}`)
    .join(';')
}
